<template>
  <div v-if="isVisible">
    <div class="modal show custom-upload" tabindex="-1" role="dialog" data-bs-backdrop="static" data-bs-keyboard="false">
      <div class="modal-dialog modal-dialog-scrollable modal-lg" role="document">
        <div class="modal-content">
          <!-- working: keep the user here until the server answers -->
          <template v-if="state === 'working'">
            <div class="modal-header">
              <h4 class="modal-title">Uploading pricing</h4>
            </div>
            <div class="modal-body text-center">
              <div class="spinner-border text-primary my-3" role="status" aria-hidden="true"></div>
              <p class="mb-1"><strong>Please wait, saving {{ modelValue.count }} rows.</strong></p>
              <p class="text-muted mb-0">Don't close or leave this page until this finishes.</p>
            </div>
          </template>

          <template v-else-if="state === 'error'">
            <div class="modal-header">
              <h4 class="modal-title">Upload failed</h4>
            </div>
            <div class="modal-body">
              <p class="custom-problem mb-0">
                <i class="fas fa-exclamation-triangle me-2"></i>{{ modelValue.message }}
              </p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-primary" @click="close()">Close</button>
            </div>
          </template>

          <template v-else-if="state === 'done'">
            <div class="modal-header">
              <h4 class="modal-title">{{ summary }}</h4>
            </div>
            <div class="modal-body">
              <template v-if="failed.length > 0">
                <p class="custom-problem">
                  <i class="fas fa-exclamation-triangle me-2"></i>
                  {{ failed.length }} {{ failed.length === 1 ? 'row was' : 'rows were' }} not saved.
                </p>
                <p>
                  <strong>{{ modelValue.fileName }}</strong> has been downloaded with just those rows and a Status column
                  saying what to fix. Correct them and upload that file; the Status column is ignored.
                </p>
                <h6>Not saved</h6>
                <div class="custom-table-holder mb-3">
                  <table class="table table-sm table-bordered mb-0">
                    <thead>
                      <tr><th>Row</th><th>Calibration</th><th>Make</th><th class="text-end">Price</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in failed" :key="'f' + item.row">
                        <td>{{ item.row }}</td>
                        <td>{{ item.calibrationTypeName }}</td>
                        <td>{{ item.vehicleMakeName }}</td>
                        <td class="text-end">{{ item.price }}</td>
                        <td class="custom-problem">{{ item.status }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <h6 v-if="saved.length > 0">Saved</h6>
              </template>
              <p v-if="raised.length > 0">
                <i class="fas fa-arrow-up me-2 custom-raised"></i>
                {{ raised.length }} {{ raised.length === 1 ? 'price was' : 'prices were' }} below the insurer minimum and
                {{ raised.length === 1 ? 'was' : 'were' }} saved at the minimum instead.
              </p>
              <p v-if="isCleanSuccess" class="mb-0">All rows were saved.</p>
              <div v-if="saved.length > 0 && !isCleanSuccess" class="custom-table-holder">
                <table class="table table-sm table-bordered mb-0">
                  <thead>
                    <tr><th>Row</th><th>Calibration</th><th>Make</th><th class="text-end">Price</th><th></th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in saved" :key="'s' + item.row">
                      <td>{{ item.row }}</td>
                      <td>{{ item.calibrationTypeName }}</td>
                      <td>{{ item.vehicleMakeName }}</td>
                      <td class="text-end">${{ formatPrice(item.price) }}</td>
                      <td :class="{ 'custom-raised': item.enteredPrice != null }">{{ item.action }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="modal-footer">
              <button v-if="failed.length > 0" type="button" class="btn btn-secondary" @click="$emit('download')">
                Download problem rows again
              </button>
              <button type="button" class="btn btn-primary" @click="close()">Close</button>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show custom-upload-back"></div>
  </div>
</template>

<script>
export default {
  name: 'PricingUploadDialog',
  props: {
    // null | { state: 'working', count } | { state: 'done', saved, failed, fileName } | { state: 'error', message }
    modelValue: {
      type: Object,
      default: null
    }
  },
  emits: ['update:modelValue', 'closing', 'download'],
  computed: {
    isVisible() {
      return this.modelValue !== null
    },
    state() {
      return this.modelValue?.state
    },
    saved() {
      return this.modelValue?.saved || []
    },
    failed() {
      return this.modelValue?.failed || []
    },
    // rows saved at the insurer floor instead of the uploaded price
    raised() {
      return this.saved.filter((item) => item.enteredPrice !== null && item.enteredPrice !== undefined)
    },
    isCleanSuccess() {
      return this.failed.length === 0 && this.raised.length === 0
    },
    summary() {
      let text = `${this.saved.length} saved`
      if (this.raised.length > 0) {
        text += ` (${this.raised.length} raised to the minimum)`
      }
      if (this.failed.length > 0) {
        text += `, ${this.failed.length} need fixing`
      }
      return text
    }
  },
  methods: {
    formatPrice(value) {
      return value === null || value === undefined ? '0.00' : parseFloat(value).toFixed(2)
    },
    close() {
      this.$emit('update:modelValue', null)
      this.$emit('closing')
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-upload {
  display: block;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1055;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  outline: 0;
}

.custom-upload-back {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  width: 100vw;
  height: 100vh;
  background-color: #000;
}

.custom-table-holder {
  max-height: 260px;
  overflow-y: auto;
}

.custom-problem {
  color: #c62828;
}

.custom-raised {
  color: #b26a00;
}
</style>
